import {useEffect, useRef, useState} from 'react';
import {type IntersectionOptions, useInView} from 'react-intersection-observer';

const DEFAULT_OPTIONS = {
  fallbackInView: true,
  rootMargin: '200px 0px',
  threshold: 0.05,
  unmountDelay: 1500,
} as const;

/**
 * Virtualize individual panels in the workspace.
 * Prevents mounting panels that are not in view and unmounts them after a delay
 * to improve fast scrolling experience.
 */
export function useIsWorkspacePanelInView(options?: IntersectionOptions) {
  const [shouldMountPanel, setShouldMountPanel] = useState(false);
  const unmountTimeoutRef = useRef<NodeJS.Timeout>(undefined);

  const config = {
    ...DEFAULT_OPTIONS,
    ...options,
    triggerOnce: false,
  };
  const {ref, inView} = useInView(config);

  useEffect(() => {
    if (inView) {
      // cancel any pending unmount timers
      if (unmountTimeoutRef.current) {
        clearTimeout(unmountTimeoutRef.current);
        unmountTimeoutRef.current = undefined;
      }

      // indicate we should mount the panel since it's in view now
      setShouldMountPanel(true);
    }
  }, [inView]);

  useEffect(() => {
    // Delay unmounting the panel since it's no longer in view
    if (!inView && shouldMountPanel) {
      if (unmountTimeoutRef.current) {
        // If we hit this, then we're attempting to override an existing timeout.
        // This is a potential leak and should be investigated.
        console.error(
          'Warning - potential timeout leak. unmountTimeoutRef is already set.'
        );
        clearTimeout(unmountTimeoutRef.current);
        unmountTimeoutRef.current = undefined;
      }

      unmountTimeoutRef.current = setTimeout(() => {
        setShouldMountPanel(false);
      }, config.unmountDelay);
    }
  }, [inView, shouldMountPanel, config.unmountDelay]);

  useEffect(() => {
    // cleanup when unmounting
    return () => {
      if (unmountTimeoutRef.current) {
        clearTimeout(unmountTimeoutRef.current);
      }
    };
  }, []);

  return {ref, shouldMountPanel} as const;
}
